import { RoomRequest } from '@shared/models/hotel-search-payload.model';
import { HotelRoom, HotelRoomWithKey, MealBasis, RoomCategory } from '@shared/models/hotel-search-response.model';
import { IProductGroupOrdering, IRoomGroupOrder } from '@shared/shared/interfaces/site-settings.interfaces';

export class RoomService {
  public static mapRoomsToHotelRoomsWithKey(
    rooms: HotelRoom[],
    roomGroups: IRoomGroupOrder[],
    pricePerRoomPerNight: boolean,
    productGroupsForBanner: IProductGroupOrdering[],
    roomRequests: RoomRequest[],
    sequenceNumber?: number
  ): HotelRoomWithKey[] {
    const resultRooms: HotelRoomWithKey[] = [];
    if (sequenceNumber) {
      rooms = rooms.filter((r) => r.seq === sequenceNumber);
    }
    rooms?.forEach((room: HotelRoom) => {
      const hotelRoomWithKey: HotelRoomWithKey = {
        key: {
          roomType: room.room_type,
          roomCategory: RoomService.getRoomCategory(room),
          nonRefundable: room.is_non_refundable,
          onRequestDCRooms: room.on_request_dc_rooms,
          payLocalRequired: room.pay_local_required,
          payByInstalments: room.instalment_payments_available,
          productCode: room.product_code,
          roomGroup: RoomService.getRoomGroup(roomGroups, room),
          productGroupForBanner: RoomService.getRoomProductGroupForBanner(productGroupsForBanner, room.product_code),
        },
        meals: [],
      };

      if (roomRequests.length > 1) {
        hotelRoomWithKey.key.supplier = room.supplier;
      }

      this.assignMealToRoom(room, hotelRoomWithKey, resultRooms, roomRequests, pricePerRoomPerNight);
    });

    return resultRooms;
  }

  private static assignMealToRoom(
    room: HotelRoom,
    hotelRoomWithKey: HotelRoomWithKey,
    resultRooms: HotelRoomWithKey[],
    roomRequests: RoomRequest[],
    pricePerRoomPerNight: boolean
  ) {
    const meal: MealBasis = {
      id: room.meal_basis,
      roomGroup: room.room_group,
      hotelRoom: room,
      productCode: room.product_code,
      specialOffers: room.special_offers,
      benefitRateDescription: RoomService.getBenefitDescriptions(room),
      price: {
        amount: pricePerRoomPerNight ? +room.price_per_night.amount : +room.total_price.amount,
        currencyCode: room.price_per_night.currency_code,
      },
    };

    const foundRooms: HotelRoomWithKey[] = resultRooms.filter(
      (item: HotelRoomWithKey) => JSON.stringify(item.key) === JSON.stringify(hotelRoomWithKey.key)
    );

    if (!foundRooms.length) {
      hotelRoomWithKey.meals.push(meal);
      resultRooms.push(hotelRoomWithKey);
      return;
    }

    let mealAdded = false;

    foundRooms.forEach((foundRoom: HotelRoomWithKey) => {
      if (mealAdded) {
        return;
      }

      const foundMealIndex: number = foundRoom.meals.findIndex((m: MealBasis) => m.id === meal.id);
      if (foundMealIndex === -1) {
        foundRoom.meals.push(meal);
        mealAdded = true;
        return;
      }

      // for single-room booking keep the cheapest price
      if (roomRequests.length === 1 && meal.price.amount < foundRoom.meals[foundMealIndex].price.amount) {
        foundRoom.meals[foundMealIndex] = meal;
        mealAdded = true;
      }
    });

    if (!mealAdded && roomRequests.length > 1) {
      hotelRoomWithKey.meals.push(meal);
      resultRooms.push(hotelRoomWithKey);
    }
  }

  private static getRoomCategory(room: HotelRoom): RoomCategory {
    let roomCategory: RoomCategory;
    if (!room.on_request_dc_rooms && !room.instalment_payments_available && !room.on_request_dc_rooms) {
      roomCategory = RoomCategory.nonDC;
    }
    if (room.on_request_dc_rooms) {
      roomCategory = RoomCategory.DC;
    }
    if (!room.on_request_dc_rooms && !room.instalment_payments_available && room.pay_local_required) {
      roomCategory = RoomCategory.AmadeusHotels;
    }
    if (room.instalment_payments_available) {
      roomCategory = RoomCategory.Instalments;
    }
    return roomCategory;
  }

  private static getRoomGroup(productGroups: IRoomGroupOrder[], room: HotelRoom): IRoomGroupOrder {
    const productGroup = productGroups?.find(
      (pg: IRoomGroupOrder) => !!room.product_code && pg.product_code.includes(room.product_code)
    );
    return (
      productGroup || {
        product_code: '',
        product_group: '',
        priority: Number.MAX_SAFE_INTEGER,
      }
    );
  }

  private static getRoomProductGroupForBanner(
    bannerProductGroups: IProductGroupOrdering[],
    roomProductCode: string
  ): string {
    return !roomProductCode
      ? ''
      : bannerProductGroups.find((pg: IProductGroupOrdering) => pg.product_code.split(',').includes(roomProductCode))
          ?.product_group;
  }

  private static getBenefitDescriptions(room: HotelRoom): string[] {
    return room.benefit_rate_description?.length
      ? room.benefit_rate_description
          ?.reduce((prev: string, curr: string) => {
            return prev.concat(',', curr);
          })
          .split(',')
          .filter((benefit: string) => !!benefit)
      : [];
  }
}
